import { motion } from "framer-motion";
import {
  Mic,
  Brain,
  FileText,
  Globe,
  Shield,
  Zap,
  Database,
  Clock,
  Search,
} from "lucide-react";
import Section from "../ui/Section";
import Card from "../ui/Card";

const features = [
  {
    icon: Mic,
    title: "Gelişmiş Konuşma Tanıma",
    description:
      "Türkçe tıbbi terminolojiye özel eğitilmiş model. %100 tıbbi terim doğruluğu. 1,900 gerçek rapor üzerinde eğitildi.",
    metric: "%95+ doğruluk",
    gradient: "from-primary-500 to-primary-600",
    bgGlow: "group-hover:shadow-glow-primary",
  },
  {
    icon: Brain,
    title: "Akıllı Metin Analizi",
    description:
      "Çok katmanlı doğal dil işleme mimarisi. Tıbbi varlık tanıma, ilişki çıkarım ve yapılandırma otomatik olarak gerçekleştirilir.",
    metric: "Otomatik",
    gradient: "from-violet-500 to-purple-600",
    bgGlow: "group-hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]",
  },
  {
    icon: Search,
    title: "Hızlı Arama Motoru",
    description:
      "Milisaniyeler içinde benzer rapor ve şablon araması. Akıllı önbellekleme ile isteklerin %75'i anında karşılanır.",
    metric: "<42ms",
    gradient: "from-sky-500 to-blue-600",
    bgGlow: "group-hover:shadow-[0_0_20px_rgba(14,165,233,0.3)]",
  },
  {
    icon: FileText,
    title: "Yapılandırılmış Raporlama",
    description:
      "SNOMED CT & ICD-10 entegrasyonu. %94 kapsama oranı, %97 doğrulama başarısı. Rapor başı maliyet: $0.03.",
    metric: "%94 kapsama",
    gradient: "from-emerald-500 to-green-600",
    bgGlow: "group-hover:shadow-glow-emerald",
  },
  {
    icon: Globe,
    title: "Çoklu Dil & Terminoloji",
    description:
      "Türkçe, İngilizce ve Latince tıbbi terminoloji. KenLM 3-gram dil modeli ile bağlamsal doğruluk.",
    metric: "3 dil",
    gradient: "from-amber-500 to-orange-600",
    bgGlow: "group-hover:shadow-glow-accent",
  },
  {
    icon: Shield,
    title: "KVKK & Güvenlik",
    description:
      "AES-256 + TLS 1.3 şifreleme. K-Anonymity k≥5, Differential Privacy ε≤1. ISO 27001 & 27799 uyumlu.",
    metric: "AES-256",
    gradient: "from-teal-500 to-cyan-600",
    bgGlow: "group-hover:shadow-glow-teal",
  },
  {
    icon: Zap,
    title: "3 Seviyeli Yanıt Sistemi",
    description:
      "Önbellek (<1ms) → Paralel arama (<80ms) → AI destekli oluşturma (<2s). Toplam yanıt: <2.1s.",
    metric: "<2.1s",
    gradient: "from-rose-500 to-pink-600",
    bgGlow: "group-hover:shadow-[0_0_20px_rgba(244,63,94,0.3)]",
  },
  {
    icon: Database,
    title: "Hibrit AI Yaklaşım",
    description:
      "Geleneksel yöntemler 10,000+ etiket gerektirirken, AI tabanlı yaklaşım ~200 örnekle aynı performansı sağlar.",
    metric: "~200 örnek",
    gradient: "from-indigo-500 to-blue-600",
    bgGlow: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.3)]",
  },
  {
    icon: Clock,
    title: "%90+ Zaman Tasarrufu",
    description:
      "Model: 360MB, inference: ~200ms. INT8 quantization ile %65 küçültme, knowledge distillation ile %12 accuracy artışı.",
    metric: "~200ms",
    gradient: "from-cyan-500 to-sky-600",
    bgGlow: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]",
  },
];

export default function Features() {
  return (
    <Section id="features" background="dots" padding="xl">
      <div className="text-center mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-display-sm md:text-display-md font-display font-bold text-gray-900 dark:text-white mb-5">
            Akademik Düzeyde{" "}
            <span className="text-gradient">Teknoloji</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            7/7 metrikte hedefe ulaşmış, gerçek tıbbi verilerle doğrulanmış performans
          </p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
            >
              <Card className={`h-full group ${feature.bgGlow}`} variant="glass">
                <div>
                  {/* Icon + Metric */}
                  <div className="flex items-center justify-between mb-5">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-lg`}
                    >
                      <Icon size={22} className="text-white" />
                    </div>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 font-mono">
                      {feature.metric}
                    </span>
                  </div>

                  <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-center mt-14"
      >
        <div className="relative overflow-hidden rounded-2xl p-8 md:p-10 bg-gradient-to-r from-primary-600 via-primary-500 to-medical-blue text-white">
          <div className="absolute inset-0 shimmer" />
          <div className="relative z-10">
            <h3 className="text-xl md:text-2xl font-display font-bold text-white mb-3">
              Tüm Metrikleri Detaylı İnceleyin
            </h3>
            <p className="text-sm opacity-90 mb-5 max-w-xl mx-auto">
              1,900 gerçek tıbbi rapor üzerinde doğrulanmış sonuçları canlı demo ile deneyimleyin
            </p>
            <button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-white text-primary-600 font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-colors duration-200 text-sm"
            >
              Detaylı Demo İsteyin
            </button>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
